import React from 'react';
import './Portfolio.css';

const industryExperiences = [
  {
    logo: 'images/imc.jpeg',
    company: 'IMC Trading',
    companyColor: '#e8a000',
    role: 'Software Engineer Intern - Low-Latency Execution',
    period: 'Jun 2026 - Aug 2026',
    location: 'Chicago, IL',
    bullets: [
      'Built ultra-low-latency quoting and execution systems on the hot path of an automated options market-making engine.'
    ],
    skills: ['C++'],
  },
  {
    logo: 'images/stripe.jpeg',
    company: 'Stripe',
    companyColor: '#635bff',
    role: 'Software Engineer Intern — Core Infrastructure',
    period: 'Jan 2026 – May 2026',
    location: 'Seattle, WA',
    bullets: [
      'Built several specialized endpoint connection managers for service mesh in Go, reducing P99 tail latency via pre-warmed connection pooling and O(1) idle connection acquisition.',
    ],
    skills: ['Go', 'Ruby', 'Grafana'],
  },
  {
    logo: 'images/zip.jpeg',
    company: 'Zip',
    companyColor: '#ff6b35',
    role: 'Software / ML Engineer Intern — AP Automation',
    period: 'May 2025 – Aug 2025',
    location: 'San Francisco, CA',
    bullets: [
      'Architected performant modular AI document processing pipeline supporting 80+ languages; achieved 15% accuracy increase across 250+ enterprise customers.',
    ],
    skills: ['Python', 'PostgreSQL', 'Braintrust', 'LaunchDarkly'],
  },
  {
    logo: 'images/verizon.png',
    company: 'Verizon',
    companyColor: '#cd040b',
    role: 'Software Engineer Intern — Network Systems',
    period: 'Jun 2024 – Aug 2024',
    location: 'Irvine, CA',
    bullets: [
      'Developed network topology visualization tool monitoring millions of customer KPIs; achieved estimated 20% reduction in MTTR (Mean Time to Repair).',
    ],
    skills: ['Java', 'Neo4j', 'GraphQL', 'MongoDB', 'PostgreSQL'],
  },
  {
    logo: 'images/berkeleytime.png',
    company: 'Berkeleytime',
    companyColor: '#14C47E',
    role: 'ML Applications Pod Lead & Backend Developer',
    period: 'Feb 2024 – Present',
    location: 'Berkeley, CA',
    bullets: [
      "Architecting scalable backend and semantic search pipeline for UC Berkeley's most popular course discovery platform (30k+ monthly users). Leading development of a novel course recommendation feature utilizing machine learning; system is tailored to individual user data and activity.",
    ],
    skills: ['Apollo GraphQL', 'MongoDB', 'Kubernetes', 'Docker', 'TypeScript', 'Python', 'Machine Learning'],
  },
];

const academiaExperiences = [
  {
    logo: 'images/berkeley.jpeg',
    company: 'UC Berkeley',
    companyColor: '#FDB515',
    role: 'Course Staff — CS 170: Efficient Algorithms & Intractable Problems',
    period: 'Aug 2025 – Dec 2025, Aug 2026 - Present',
    location: 'Berkeley, CA',
    bullets: [
      "Helping teach CS 170 - Efficient Algorithms & Intractable Problems",
    ],
    skills: ['Teaching', 'Programming', 'Algorithms', 'Mathematics'],
  },
  {
    logo: 'images/redwood.jpg',
    company: 'Redwood Center for Theoretical Neuroscience (BAIR)',
    companyColor: '#e07b39',
    role: 'Student Researcher',
    period: 'Aug 2024 – Jan 2025',
    location: 'Berkeley, CA',
    bullets: [
      'Engineered high-performance Ising model solvers for combinatorial optimization problems (k-SAT); benchmarked models with JAX achieving 30% speedup over baseline methods.',
    ],
    skills: ['Google JAX', 'Machine Learning', 'Optimization', 'Python'],
  },
  {
    logo: 'images/berkeley.jpeg',
    company: 'UC Berkeley',
    companyColor: '#FDB515',
    role: 'Instructor — CS 198: Esoteric Programming Languages',
    period: 'Jan 2025 – May 2025',
    location: 'Berkeley, CA',
    bullets: [
      'Instructor for the 2-unit DeCal (student-run class) CS 198 - Esoteric Programming Languages',
    ],
    skills: ['Teaching', 'Programming', 'Algorithms'],
  },
  {
    logo: 'images/berkeley.jpeg',
    company: 'UC Berkeley',
    companyColor: '#FDB515',
    role: 'Course Staff — CS 70: Discrete Math & Probability Theory',
    period: 'Aug 2024 – May 2025',
    location: 'Berkeley, CA',
    bullets: [
      'Helping teach CS 70 - Discrete Mathematics & Probability Theory',
    ],
    skills: ['Teaching', 'Mathematics', 'Algorithms'],
  },
];

const projects = [
  {
    title: 'Berkeley Room Finder',
    description: "An easy-to-use web app that helps students find empty study rooms on campus. Pulls availability directly from Berkeley's course listing website.",
    image: 'images/brf.png',
    url: 'https://github.com/subhashjprasad/room-finder',
    skills: ['Python', 'Selenium', 'MongoDB', 'Leaflet.js'],
  },
  {
    title: 'Bop',
    description: 'An esoteric programming language where code is "written" by pressing inputs according to predetermined rhythms. Final project for CS 198.',
    image: 'images/bop.png',
    url: 'https://github.com/subhashjprasad/bop',
    skills: ['Java', 'Programming'],
  },
  {
    title: 'Graversal',
    description: 'A rage-inducing, gravity-switching, eye-popping mobile platformer game.',
    image: 'images/grav.png',
    url: 'https://github.com/subhashjprasad/graversal',
    skills: ['GML (GameMakerLanguage)', 'Game Development'],
  },
  {
    title: 'Lecturix',
    description: 'An LLM pipeline to convert lecture notes into full-fledged animated lectures.',
    image: 'images/lecturix.png',
    url: 'https://github.com/subhashjprasad/lecturix',
    skills: ['Python'],
  },
];

function LogoAvatar({ logo, logoText, logoColor }) {
  if (logo) {
    return <img src={logo} alt="logo" className="exp-logo" />;
  }
  return (
    <div className="exp-logo-avatar" style={{ background: logoColor }}>
      {logoText}
    </div>
  );
}

function ExperienceCard({ exp }) {
  return (
    <div className="exp-card">
      <div className="exp-card-header">
        <LogoAvatar logo={exp.logo} logoText={exp.logoText} logoColor={exp.logoColor} />
        <div className="exp-card-title">
          <span className="exp-role">{exp.role}</span>
          <span className="exp-company">{exp.company}</span>
          {exp.period && <span className="exp-period">{exp.period}</span>}
          {exp.location && <span className="exp-location">{exp.location}</span>}
        </div>
      </div>
      {exp.bullets.length > 0 && (
        <div className="exp-bullets">
          {exp.bullets.map((b, i) => <p key={i}>{b}</p>)}
        </div>
      )}
      {exp.skills.length > 0 && (
        <div className="exp-skills">
          {exp.skills.map(s => <span className="skill-tag" key={s}>{s}</span>)}
        </div>
      )}
    </div>
  );
}

function ProjectCard({ proj }) {
  return (
    <div className="proj-card" onClick={() => window.open(proj.url, '_blank', 'noopener,noreferrer')}>
      <div className="proj-card-header">
        <img src={proj.image} alt={proj.title} className="proj-icon" />
        <h3 className="proj-title">{proj.title}</h3>
      </div>
      <p className="proj-desc">{proj.description}</p>
      <div className="exp-skills">
        {proj.skills.map(s => <span className="skill-tag" key={s}>{s}</span>)}
      </div>
    </div>
  );
}

export default function Portfolio({ launch3D }) {
  return (
    <div className="portfolio">

      {/* Nav */}
      <nav className="port-nav">
        <button className="btn-3d" onClick={launch3D}>
          Enter 3D Experience ↗
        </button>
      </nav>

      {/* Hero */}
      <header className="port-hero">
        <div className="port-hero-left">
          <h1 className="port-name">Subhash Prasad</h1>
          <p className="port-subtitle">EECS @ UC Berkeley</p>
          <div className="port-links">
            <a href="https://linkedin.com/in/subhash-j-prasad" target="_blank" rel="noreferrer">
              <img src="images/linkedin.png" alt="LinkedIn" className="social-icon" />
            </a>
            <a href="https://github.com/subhashjprasad" target="_blank" rel="noreferrer">
              <img src="images/github.png" alt="GitHub" className="social-icon" />
            </a>
          </div>
        </div>
        <img src="images/pfp-face.jpg" alt="Subhash Prasad" className="port-pfp" />
      </header>

      {/* Education */}
      <section className="port-section">
        <h2 className="section-title">Education</h2>
        <div className="section-accent" />
        <div className="exp-card">
          <div className="exp-card-header">
            <img src="images/berkeley.jpeg" alt="UC Berkeley" className="exp-logo" />
            <div className="exp-card-title">
              <span className="exp-role">B.S. Electrical Engineering and Computer Sciences</span>
              <span className="exp-company">University of California, Berkeley</span>
              <span className="exp-period">Aug 2023 – May 2027 · GPA: 3.92</span>
              <span className="exp-location">Eta Kappa Nu (HKN) &nbsp;·&nbsp; IEEE &nbsp;·&nbsp; CSUA</span>
            </div>
          </div>
        </div>
      </section>

      {/* Experience */}
      <section className="port-section">
        <div className="port-columns">
          <div className="port-col">
            <h2 className="section-title">Industry</h2>
            <div className="section-accent" />
            {industryExperiences.map((exp, i) => <ExperienceCard key={i} exp={exp} />)}
          </div>
          <div className="port-col">
            <h2 className="section-title">Academia</h2>
            <div className="section-accent" />
            {academiaExperiences.map((exp, i) => <ExperienceCard key={i} exp={exp} />)}
          </div>
        </div>
      </section>

      {/* Projects */}
      <section className="port-section">
        <h2 className="section-title">Projects</h2>
        <div className="section-accent" />
        <div className="proj-grid">
          {projects.map((proj, i) => <ProjectCard key={i} proj={proj} />)}
        </div>
      </section>

      <footer className="port-footer">
        <p>© 2026 Subhash Prasad</p>
      </footer>

    </div>
  );
}
